import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, Package, Users, TrendingUp, AlertTriangle, CheckCircle, Clock, Activity, BarChart3, Shield, Zap, ArrowRight, Plus, Settings } from "lucide-react";
import axios from "axios";
import { UserContext } from "../../contexts/UserContext";

const Equipment = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equipmentData, setEquipmentData] = useState({
    stats: null
  });

  // API Base URL
  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  // Navigation handlers
  const handleNavigateToManagement = () => {
    navigate('/equipment/management');
  };

  const handleNavigateToActiveAssignments = () => {
    navigate('/equipment/management', {
      state: {
        presetFilter: {
          userFilter: ['__all_assignments__']
        }
      }
    });
  };

  const handleNavigateToAssignments = () => {
    navigate('/equipment/assignments');
  };

  const handleNavigateToInspectionExpiring = () => {
    navigate('/equipment/management', {
      state: {
        presetFilter: {
          validityFilter: ['expired', 'used_without_inspection', 'next30']
        }
      }
    });
  };

  const handleNavigateToManufacturerExpiring = () => {
    navigate('/equipment/management', { 
      state: { 
        presetFilter: { 
          manufacturerValidityFilter: ['expired', 'next30'] 
        } 
      } 
    });
  };

  // Helper functions for validity calculations
  const getInspectionValidityStatus = (item) => {
    if (!item.latest_inspection?.validity_date) {
      return {
        hasData: false,
        daysUntilExpiry: null,
        status: 'no_inspection'
      };
    }
    
    const today = new Date();
    const validityDate = new Date(item.latest_inspection.validity_date);
    const timeDiff = validityDate.getTime() - today.getTime();
    const daysUntilExpiry = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
    let status = 'valid';
    if (daysUntilExpiry < 0) {
      status = 'expired';
    } else if (daysUntilExpiry <= 30) {
      status = 'expires_soon';
    } else if (daysUntilExpiry <= 60) {
      status = 'expires_medium';
    }
    
    return {
      hasData: true,
      daysUntilExpiry,
      status,
      validityDate
    };
  };

  const getManufacturerValidityStatus = (item) => {
    // Use cached manufacturer_validity_date from backend
    const validityDate = item.manufacturer_validity_date;

    // NULL means model has no time_to_manufacturer_inspection set
    if (!validityDate) {
      return {
        hasData: false,
        validityDate: null,
        daysUntilExpiry: null,
        status: 'no_data'
      };
    }

    // Parse validity date from backend
    const validityDateTime = new Date(validityDate);
    const today = new Date();
    const timeDiff = validityDateTime.getTime() - today.getTime();
    const daysUntilExpiry = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    // Check if this is "lifetime" validity (>50 years in the future)
    // 50 years * 365 days = 18,250 days
    if (daysUntilExpiry > 18250) {
      return {
        hasData: true,
        validityDate: validityDateTime,
        daysUntilExpiry,
        status: 'lifetime',
        isLifetime: true
      };
    }

    // Normal validity date
    let status = 'valid';
    if (daysUntilExpiry < 0) {
      status = 'expired';
    } else if (daysUntilExpiry <= 30) {
      status = 'expires_soon';
    } else if (daysUntilExpiry <= 60) {
      status = 'expires_medium';
    }

    return {
      hasData: true,
      validityDate: validityDateTime,
      daysUntilExpiry,
      status
    };
  };

  // Helper function to get current user's company ID
  const getCurrentUserCompanyId = () => {
    if (user && user.company) {
      // If company is already a number (ID), return it directly
      if (typeof user.company === 'number') {
        return user.company;
      }
      // If company is an object, get its ID
      if (typeof user.company === 'object' && user.company.id) {
        return user.company.id;
      }
      // If company is a string number, parse it
      if (typeof user.company === 'string' && !isNaN(user.company)) {
        return parseInt(user.company);
      }
    }
    return null;
  };

  // Helper function to check if user's company can do external inspections
  const userCanDoExternalInspections = () => {
    return equipmentData.stats?.user_can_inspection || false;
  };

  // Get counts from aggregated stats - much faster!
  const getInspectionTotalCount = () => {
    const expired = equipmentData.stats?.inspection_expired_count || 0;
    const expiring = equipmentData.stats?.inspection_expiring_count || 0;
    return expired + expiring;
  };

  const getManufacturerTotalCount = () => {
    const expired = equipmentData.stats?.manufacturer_expired_count || 0;
    const expiring = equipmentData.stats?.manufacturer_expiring_count || 0;
    return expired + expiring;
  };

  const getTotalEquipmentCount = () => {
    return equipmentData.stats?.total_equipment_count || 0;
  };

  const getOwnActiveAssignmentsCount = () => {
    return equipmentData.stats?.own_active_assignments_count || 0;
  };


  // Fetch all equipment data
  useEffect(() => {
    const fetchEquipmentData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('access_token');
        const config = {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        };

        console.log('🔄 Fetching dashboard statistics...');

        // Fetch only aggregated stats for dashboard - much faster!
        const statsResponse = await axios.get(`${API_BASE_URL}/safetyequipment/dashboard-stats/`, config);

        const data = {
          stats: statsResponse.data
        };

        setEquipmentData(data);

        // Console logging for dashboard stats
        console.log('📊 Dashboard Stats:', data.stats);

        // DEBUG: Log inspection problem items
        if (data.stats.debug_expired_items) {
          console.log('🔴 EXPIRED items:', data.stats.debug_expired_items);
        }
        if (data.stats.debug_expiring_items) {
          console.log('🟡 EXPIRING items:', data.stats.debug_expiring_items);
        }
        if (data.stats.debug_critical_items) {
          console.log('⚠️ CRITICAL items (no inspection + issued/received):', data.stats.debug_critical_items);
        }

        const totalProblemItems =
          (data.stats.debug_expired_items?.length || 0) +
          (data.stats.debug_expiring_items?.length || 0) +
          (data.stats.debug_critical_items?.length || 0);
        console.log(`📝 Total problem items found: ${totalProblemItems}`);

      } catch (error) {
        console.error('❌ Error fetching equipment data:', error);
        setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch equipment data');
      } finally {
        setLoading(false);
      }
    };

    fetchEquipmentData();
  }, [API_BASE_URL]);

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mb-4"></div>
        <p className="text-slate-600">Loading equipment data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-xl shadow-lg shadow-brand-500/20 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const renderContent = () => {
    return (
      <div className="space-y-8">
        {/* Analytics Dashboard */}
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Equipment Analytics</h2>
            <p className="text-sm text-slate-500 mt-1">Overview of equipment status and alerts</p>
          </div>

          {/* Primary Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Critical Alerts Card */}
            <div
              className="group bg-white rounded-xl p-5 shadow-sm border-2 border-steel-200 cursor-pointer hover:shadow-lg hover:border-red-300 transition-all duration-200"
              onClick={handleNavigateToInspectionExpiring}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 bg-red-50 rounded-xl">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <span className="text-[10px] text-red-600 font-semibold bg-red-50 px-2 py-1 rounded-lg">CRITICAL</span>
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {!loading ? getInspectionTotalCount() : '...'}
              </div>
              <h3 className="font-medium text-slate-900">Inspections Expired/Expiring</h3>
              <p className="text-xs text-slate-500 mt-1">Expired + Next 30 days</p>
            </div>

            {/* Manufacturer Warranty Card */}
            <div
              className="group bg-white rounded-xl p-5 shadow-sm border-2 border-steel-200 cursor-pointer hover:shadow-lg hover:border-orange-300 transition-all duration-200"
              onClick={handleNavigateToManufacturerExpiring}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 bg-orange-50 rounded-xl">
                  <Shield className="h-6 w-6 text-orange-600" />
                </div>
                <span className="text-[10px] text-orange-600 font-semibold bg-orange-50 px-2 py-1 rounded-lg">WARNING</span>
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {!loading ? getManufacturerTotalCount() : '...'}
              </div>
              <h3 className="font-medium text-slate-900">Warranty Expired/Expiring</h3>
              <p className="text-xs text-slate-500 mt-1">Expired + Next 30 days</p>
            </div>

            {/* Total Equipment Card */}
            <div
              className="group bg-white rounded-xl p-5 shadow-sm border-2 border-steel-200 cursor-pointer hover:shadow-lg hover:border-brand-300 transition-all duration-200"
              onClick={handleNavigateToManagement}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 bg-brand-50 rounded-xl">
                  <Package className="h-6 w-6 text-brand-600" />
                </div>
                <span className="text-[10px] text-brand-600 font-semibold bg-brand-50 px-2 py-1 rounded-lg">TOTAL</span>
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {!loading ? getTotalEquipmentCount() : '...'}
              </div>
              <h3 className="font-medium text-slate-900">All Equipment</h3>
              <p className="text-xs text-slate-500 mt-1">Total inventory</p>
            </div>

            {/* Active Assignments Card */}
            <div
              className="group bg-white rounded-xl p-5 shadow-sm border-2 border-steel-200 cursor-pointer hover:shadow-lg hover:border-green-300 transition-all duration-200"
              onClick={handleNavigateToActiveAssignments}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2.5 bg-green-50 rounded-xl">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
                <span className="text-[10px] text-green-600 font-semibold bg-green-50 px-2 py-1 rounded-lg">ACTIVE</span>
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {!loading ? getOwnActiveAssignmentsCount() : '...'}
              </div>
              <h3 className="font-medium text-slate-900">Active Assignments</h3>
              <p className="text-xs text-slate-500 mt-1">Equipment in use</p>
            </div>
          </div>

        </div>

        {/* Management Actions */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Quick Actions</h2>
            <p className="text-sm text-slate-500 mt-1">Equipment lifecycle management tools</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Equipment Management Card */}
            <a
              href="/equipment/management"
              className="group bg-white rounded-xl p-5 border-2 border-steel-200 hover:border-brand-300 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-50 rounded-lg">
                    <Package className="h-5 w-5 text-brand-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Equipment Management</h3>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" />
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Inventory tracking, inspection scheduling, and compliance reporting.
              </p>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Plus className="h-3 w-3" /> Add</span>
                <span className="flex items-center gap-1"><Settings className="h-3 w-3" /> Configure</span>
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Track</span>
              </div>
            </a>

            {/* Assignment Management Card */}
            <a
              href="/equipment/management"
              className="group bg-white rounded-xl p-5 border-2 border-steel-200 hover:border-brand-300 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-steel-100 rounded-lg">
                    <Users className="h-5 w-5 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Assignment Management</h3>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" />
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Equipment distribution, user assignments, and utilization analytics.
              </p>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> Assign</span>
                <span className="flex items-center gap-1"><Activity className="h-3 w-3" /> Usage</span>
                <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Analytics</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-50 to-steel-50 border-b border-steel-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-white rounded-xl shadow-sm border border-steel-200">
                <HardHat className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Equipment Dashboard</h1>
                <p className="text-sm text-slate-500">Comprehensive equipment management platform</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Equipment;